import {
    ActionIcon,
    AppShell,
    Avatar,
    Badge,
    Box,
    Burger,
    Drawer,
    Group,
    Image,
    Menu,
    rem,
    Stack,
    Text,
    Title,
    Tooltip,
    useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconInfoCircle, IconUser, IconX } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet, redirect, useLocation, useNavigate } from "@tanstack/react-router";
import { Suspense, useContext, useEffect } from "react";
import { logoMaxSize } from "routes/account/route";
import { CenteredBryxLoader } from "shared/components/global/Loaders/BryxLoader";
import { CenteredLoader } from "shared/components/global/Loaders/CenteredLoader";
import { NavbarFooter, Sidebar, useActiveSublink, useSublinkCount } from "shared/components/global/Sidebar/Sidebar";
import { customerQueryOptions } from "shared/graphql/customer";
import { features } from "shared/graphql/features";
import { userQueryOptions, useUser } from "shared/graphql/users";
import { useColorMode } from "shared/hooks/useColorMode";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { isSupport, useIsSupport } from "shared/hooks/useIsSupport";
import { isEmbedded } from "shared/interop";
import { useLayoutStore } from "shared/stores/layoutStore";
import { getUserId } from "shared/stores/oidc";
import { AsideCtx } from "shared/utils/aside";
import { logger } from "shared/utils/logger";
import { maybe } from "shared/utils/maybe";
import { UserStatus } from "src/gql/graphql";
import { queryClient } from "src/queryClient";
import z from "zod";

const AuthLayout = () => {
    features.useLoader();

    const {
        data: { customer },
    } = useSuspenseQuery(customerQueryOptions.whiteLabeling);

    const nav = useNavigate();
    const { pathname } = useLocation();
    const isMobile = useIsMobile();
    const theme = useMantineTheme();
    const { isDark } = useColorMode();
    const [opened, { toggle, close }] = useDisclosure(true);
    const { isSupport } = useIsSupport();
    const { user } = useUser();

    const activeSublink = useActiveSublink();
    const mainSublinkCount = useSublinkCount();

    useEffect(() => {
        close();
    }, [isMobile, pathname]);

    const aside = useContext(AsideCtx);
    const { asideWidth, setAsideWidth } = useLayoutStore((state) => ({
        asideWidth: state.asideWidth,
        setAsideWidth: state.setAsideWidth,
    }));

    function startAsideResize(event: React.PointerEvent<HTMLDivElement>) {
        if (isMobile || event.button !== 0) return;

        event.preventDefault();

        function handleMove(moveEvent: PointerEvent) {
            const delta = event.clientX - moveEvent.clientX;
            const nextWidth = asideWidth + delta;
            setAsideWidth(nextWidth);
        }

        function stopResizing() {
            window.removeEventListener("pointermove", handleMove);
            window.removeEventListener("pointerup", stopResizing);
            window.removeEventListener("pointercancel", stopResizing);
        }

        window.addEventListener("pointermove", handleMove);
        window.addEventListener("pointerup", stopResizing);
        window.addEventListener("pointercancel", stopResizing);
    }

    return (
        <>
            <AppShell
                header={{ height: isEmbedded() ? 0 : rem(58) }}
                footer={{ height: isMobile && !isEmbedded() && mainSublinkCount > 1 ? rem(58) : 0 }}
                navbar={{
                    width: mainSublinkCount == 1 ? 0 : (activeSublink?.data.length ?? 0) > 0 ? 225 : 50,
                    breakpoint: theme.breakpoints.sm,
                    collapsed: { mobile: !opened },
                }}
                aside={{
                    width: rem(asideWidth),
                    breakpoint: theme.breakpoints.sm,
                    collapsed: { desktop: !aside?.content || aside.mode != "aside", mobile: true },
                }}
            >
                {!isEmbedded() && (
                    <AppShell.Header>
                        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
                            {(activeSublink?.data.length ?? 0) > 1 && mainSublinkCount > 1 && (
                                <Burger
                                    opened={opened}
                                    onClick={toggle}
                                    display={!isMobile ? "none" : undefined}
                                    size="sm"
                                    aria-label="Show page navigation"
                                />
                            )}
                            <Group pl="xs" wrap="nowrap">
                                <Image
                                    src={`${import.meta.env.VITE_API_BASE_URL}/customers/${customer.id}/icon`}
                                    fallbackSrc={"/bryx.svg"}
                                    w={logoMaxSize}
                                    h={logoMaxSize}
                                    fit="contain"
                                    alt="Brand logo"
                                />
                                <Text component="h2" lts="-0.05em" w="fit-content" truncate="end" lineClamp={1}>
                                    {customer.name}
                                </Text>
                            </Group>
                            <Group>
                                {isSupport && !isMobile && (
                                    <Badge
                                        rightSection={
                                            <Tooltip label="You are logged in as a support user. You have full access to this customer.">
                                                <IconInfoCircle size={12} />
                                            </Tooltip>
                                        }
                                    >
                                        Support Admin
                                    </Badge>
                                )}
                                <Menu withInitialFocusPlaceholder={false}>
                                    <Menu.Target>
                                        <ActionIcon radius="xl" size={36} aria-label="Profile">
                                            {user?.name?.at(0) ?? <IconUser size={16} />}
                                        </ActionIcon>
                                    </Menu.Target>
                                    <Menu.Dropdown>
                                        <Menu.Label p="xs">
                                            <Group align="center" justify="center" gap="xs">
                                                <Avatar radius="xl" size={36} color="brand" variant="filled">
                                                    {user?.name?.at(0) ?? <IconUser size={16} />}
                                                </Avatar>
                                                <Stack gap={0}>
                                                    <Text>{user?.name ?? "No name"}</Text>
                                                    <Text>{user?.loginEmail}</Text>
                                                </Stack>
                                            </Group>
                                        </Menu.Label>
                                        <Menu.Divider />
                                        <Link to="/messages/inbox">
                                            <Menu.Item>Inbox</Menu.Item>
                                        </Link>
                                        <Link to="/settings" hash="profile">
                                            <Menu.Item>Profile Information</Menu.Item>
                                        </Link>
                                        {maybe(window.interop?.getProfileOptions?.())
                                            ?.map((it) => JSON.parse(it))
                                            ?.map((it) => z.object({ id: z.string(), label: z.string() }).array().safeParse(it).data)
                                            ?.take((it) => (
                                                <>
                                                    <Menu.Divider />
                                                    {it.map((each) => (
                                                        <Menu.Item
                                                            key={each.id}
                                                            onClick={() => window.interop?.onProfileOptionClick?.(each.id)}
                                                        >
                                                            {each.label}
                                                        </Menu.Item>
                                                    ))}
                                                    <Menu.Divider />
                                                </>
                                            ))}
                                        <Menu.Item onClick={() => void nav({ to: "/logout" })}>Log out</Menu.Item>
                                    </Menu.Dropdown>
                                </Menu>
                            </Group>
                        </Group>
                    </AppShell.Header>
                )}
                {!isEmbedded() && mainSublinkCount > 1 && (
                    <AppShell.Navbar aria-label="Primary navigation">
                        <Sidebar />
                    </AppShell.Navbar>
                )}
                <AppShell.Aside>
                    {!!aside?.content && !isMobile && aside.mode == "aside" && (
                        <>
                            <Box
                                role="separator"
                                aria-orientation="vertical"
                                aria-label="Resize aside"
                                onPointerDown={startAsideResize}
                                pos="absolute"
                                top={0}
                                left={0}
                                h="100%"
                                w={4}
                                bg="gray-accent"
                                style={{ cursor: "col-resize" }}
                            />
                            <Stack p="md" h="100%" mah="100%" style={{ overflow: "auto" }}>
                                <Group w="100%" justify="space-between">
                                    <Title order={3}>{aside?.label}</Title>
                                    <ActionIcon variant="transparent" onClick={() => aside.dismiss()} aria-label="Close side content">
                                        <IconX size={16} />
                                    </ActionIcon>
                                </Group>
                                <Suspense fallback={<CenteredLoader />}>{aside?.content}</Suspense>
                            </Stack>
                        </>
                    )}
                </AppShell.Aside>
                <AppShell.Main
                    style={{
                        height: isMobile ? "calc(100dvh - 116px)" : "92dvh",
                        maxHeight: isMobile ? "calc(100dvh - 116px)" : "92dvh",
                        background: isDark ? theme.colors.dark[6] : theme.colors.gray[2],
                    }}
                >
                    <Box w="100%" h="100%" style={{ overflowY: "auto" }}>
                        <Drawer
                            size="auto"
                            position="right"
                            opened={!!aside?.content && !isMobile && aside.mode == "drawer"}
                            onClose={() => aside?.dismiss()}
                            title={aside?.label}
                        >
                            <Suspense fallback={<CenteredLoader />}>{aside?.content}</Suspense>
                        </Drawer>
                        <Suspense fallback={<CenteredLoader />}>
                            <Outlet />
                        </Suspense>
                    </Box>
                </AppShell.Main>
                {!isEmbedded() && mainSublinkCount > 1 && (
                    <AppShell.Footer h="fit-content" display={!isMobile ? "none" : undefined}>
                        <NavbarFooter />
                    </AppShell.Footer>
                )}
            </AppShell>
        </>
    );
};

/** @public */
export const Route = createFileRoute("/_auth")({
    beforeLoad: async ({ context, location }) => {
        if (!!window.interop) {
            logger.log("embedded mode: skipping auth check");
            return;
        }

        const auth = await context.auth;
        if (!auth.isAuthenticated) throw redirect({ to: "/login", search: { redirect: location.href } });

        // Support people will not be associated with a user
        if (!isSupport(auth)) {
            const data = await queryClient.ensureQueryData(userQueryOptions({ userId: getUserId(auth), isSupport: false }));
            if (data?.user.status != UserStatus.Active) throw redirect({ to: "/account/register" });
        }
    },
    component: AuthLayout,
    pendingComponent: () => <CenteredBryxLoader />,
});
